import { createContext } from "preact";
import { useContext, useMemo, useState, useCallback } from "preact/hooks";
import axios from "axios";
import { useProjectDetailPage } from "../store";

const isDev = import.meta.env.DEV;
const buildUrl = (p) => (isDev ? `http://localhost:3000${p}` : p);

const AppsCardContext = createContext(null);
export const useAppsCard = () => {
  const ctx = useContext(AppsCardContext);
  if (!ctx) throw new Error("useAppsCard debe usarse dentro de <AppsCardProvider>");
  return ctx;
};

export function AppsCardProvider({ children }) {
  const { projectId, project, role, applySessionHeader, fetchProject } = useProjectDetailPage();

  const apps = useMemo(() => project?.apps || [], [project]);
  const [expanded, setExpanded] = useState({});

  const [appDlgOpen, setAppDlgOpen] = useState(false);
  const [appBusy, setAppBusy] = useState(false);
  const [appForm, setAppForm] = useState({ name: "", description: "", appUrl: "" });

  const [taskDlgOpen, setTaskDlgOpen] = useState(false);
  const [taskBusy, setTaskBusy] = useState(false);
  const [taskForm, setTaskForm] = useState({ appId: null, name: "", description: "", porcent: 0 });

  const [editTaskDlgOpen, setEditTaskDlgOpen] = useState(false);
  const [editTaskBusy, setEditTaskBusy] = useState(false);
  const [editTaskForm, setEditTaskForm] = useState({ taskId: null, porcent: 0 });

  const resetAppForm = useCallback(() => setAppForm({ name: "", description: "", appUrl: "" }), []);
  const resetTaskForm = useCallback(() => setTaskForm({ appId: null, name: "", description: "", porcent: 0 }), []);
  const resetEditTaskForm = useCallback(() => setEditTaskForm({ taskId: null, porcent: 0 }), []);

  const toggleExpand = useCallback((appId) => {
    setExpanded((e) => ({ ...e, [appId]: !e[appId] }));
  }, []);

  const openAppUrl = useCallback((appUrl) => {
    if (!appUrl) return;
    window.open(appUrl, "_blank", "noopener,noreferrer");
  }, []);

  const submitCreateApp = useCallback(async () => {
    if (!projectId || !appForm.name.trim()) return { ok: false, message: "Datos incompletos" };
    setAppBusy(true);
    applySessionHeader?.();
    try {
      const res = await axios.post(
        buildUrl(`/${role}/api/projects/apps/create`),
        { projectId, ...appForm },
        { withCredentials: true, validateStatus: () => true }
      );
      const ok = !!res?.data?.ok;
      const message = res?.data?.message || (ok ? "App creada" : "No se pudo crear");
      if (ok) {
        await fetchProject?.();
        resetAppForm();
        setAppDlgOpen(false);
      }
      return { ok, message };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.message || e?.message || "Error creando app" };
    } finally {
      setAppBusy(false);
    }
  }, [projectId, appForm, role, applySessionHeader, fetchProject, resetAppForm]);

  const submitCreateTask = useCallback(async () => {
    if (!taskForm.appId || !taskForm.name.trim()) return { ok: false, message: "Datos incompletos" };
    setTaskBusy(true);
    applySessionHeader?.();
    try {
      const res = await axios.post(
        buildUrl(`/${role}/api/projects/apps/create/task`),
        { ...taskForm, porcent: Number(taskForm.porcent) || 0 },
        { withCredentials: true, validateStatus: () => true }
      );
      const ok = !!res?.data?.ok;
      const message = res?.data?.message || (ok ? "Task creada" : "No se pudo crear");
      if (ok) {
        await fetchProject?.();
        resetTaskForm();
        setTaskDlgOpen(false);
      }
      return { ok, message };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.message || e?.message || "Error creando task" };
    } finally {
      setTaskBusy(false);
    }
  }, [taskForm, role, applySessionHeader, fetchProject, resetTaskForm]);

  const submitUpdateTaskPorcent = useCallback(async () => {
    if (!editTaskForm.taskId) return { ok: false, message: "Falta taskId" };
    setEditTaskBusy(true);
    applySessionHeader?.();
    try {
      const res = await axios.post(
        buildUrl(`/${role}/api/projects/apps/task/update-porcent`),
        { taskId: editTaskForm.taskId, porcent: Number(editTaskForm.porcent) || 0 },
        { withCredentials: true, validateStatus: () => true }
      );
      const ok = !!res?.data?.ok;
      const message = res?.data?.message || (ok ? "Porcentaje actualizado" : "No se pudo actualizar");
      if (ok) {
        await fetchProject?.();
        resetEditTaskForm();
        setEditTaskDlgOpen(false);
      }
      return { ok, message };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.message || e?.message || "Error actualizando porcentaje" };
    } finally {
      setEditTaskBusy(false);
    }
  }, [editTaskForm, role, applySessionHeader, fetchProject, resetEditTaskForm]);

  const value = useMemo(() => ({
    apps,
    expanded,
    toggleExpand,
    openAppUrl,
    role,

    appDlgOpen, appBusy, appForm,
    openCreateAppModal: () => setAppDlgOpen(true),
    closeCreateAppModal: () => { if (!appBusy) setAppDlgOpen(false); },
    setAppForm, submitCreateApp,

    taskDlgOpen, taskBusy, taskForm,
    openCreateTaskModal: (appId) => { setTaskForm((f) => ({ ...f, appId, name: "", description: "", porcent: 0 })); setTaskDlgOpen(true); },
    closeCreateTaskModal: () => { if (!taskBusy) setTaskDlgOpen(false); },
    setTaskForm, submitCreateTask,

    editTaskDlgOpen, editTaskBusy, editTaskForm,
    openEditTaskModal: (task) => { setEditTaskForm({ taskId: task._id, porcent: Number(task.porcent || 0) }); setEditTaskDlgOpen(true); },
    closeEditTaskModal: () => { if (!editTaskBusy) setEditTaskDlgOpen(false); },
    setEditTaskForm, submitUpdateTaskPorcent,
  }), [
    apps, expanded, toggleExpand, openAppUrl, role,
    appDlgOpen, appBusy, appForm, setAppForm, submitCreateApp,
    taskDlgOpen, taskBusy, taskForm, submitCreateTask,
    editTaskDlgOpen, editTaskBusy, editTaskForm, submitUpdateTaskPorcent,
  ]);

  return <AppsCardContext.Provider value={value}>{children}</AppsCardContext.Provider>;
}